import {
  SyntheticEvent, useCallback, useState, useMemo,
} from 'react';
import { buildImageSrc } from 'src/common/utils/imageHelpers';
import type { Photo } from 'src/common/types/api/v4/boats/search';

// Approximate landscape aspect ratio (~16:10) used to derive image width
// from the available lightbox height when requesting resized images.
const IMAGE_ASPECT_RATIO_MULTIPLIER = 1.6;

type UsePhotoLightboxOptions = {
  headline: string;
  photos: Photo[];
};

type UsePhotoLightboxReturn = {
  lightboxOpen: boolean;
  lightboxStartIndex: number;
  lightboxImages: { src: string; alt: string }[];
  openLightbox: (
    event?: SyntheticEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>,
    index?: number,
  ) => void;
  closeLightbox: () => void;
};

/**
 * Custom hook to manage photo lightbox behavior.
 * Supports both gallery page navigation (when feature flag is enabled)
 * and direct lightbox modal display.
 */
const usePhotoLightbox = ({
  headline,
  photos,
}: UsePhotoLightboxOptions): UsePhotoLightboxReturn => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxStartIndex, setLightboxStartIndex] = useState(0);

  const lightboxImages = useMemo(
    () => {
      const hasWindow = typeof window !== 'undefined';
      const maxLightboxHeight = hasWindow ? document.body.clientHeight * 0.75 : 0;
      const imageParamsForLightbox = {
        w: hasWindow
          ? Math.ceil(maxLightboxHeight * IMAGE_ASPECT_RATIO_MULTIPLIER)
          : 400,
      };

      return photos.map(({ url, description }) => ({
        src: buildImageSrc(url, imageParamsForLightbox),
        alt: description || headline,
      }));
    },
    [photos, headline],
  );

  const openLightbox = useCallback((
    event?: SyntheticEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>,
    index: number = 0,
  ) => {
    event?.preventDefault();
    // Open lightbox modal directly
    setLightboxStartIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  return {
    lightboxOpen,
    lightboxStartIndex,
    lightboxImages,
    openLightbox,
    closeLightbox,
  };
};

export default usePhotoLightbox;
